import type {
  Address,
  Annotation,
  Attachment,
  CodeableConcept,
  Coding,
  ContactPoint,
  HumanName,
  Identifier,
  Period,
  Quantity,
  Range,
  Ratio,
  Reference,
} from '@medplum/fhirtypes';
import isNil from 'lodash/isNil';
import type { ReactNode } from 'react';

import { EM_DASH } from '../core/copy';

/**
 * One small display component per FHIR datatype. Curated views use them
 * directly; the schema-driven generic view dispatches into them by the
 * element's declared type.
 */

export function QuantityDisplay({ value }: { value: Quantity }) {
  return (
    <span>
      {[value.comparator, value.value, value.unit ?? value.code]
        .filter((part) => part !== undefined)
        .join(' ')}
    </span>
  );
}

export function CodeableConceptDisplay({ value }: { value: CodeableConcept }) {
  return (
    <span>
      {value.text ??
        value.coding?.[0]?.display ??
        value.coding?.[0]?.code ??
        EM_DASH}
    </span>
  );
}

export function CodingDisplay({ value }: { value: Coding }) {
  return <span>{value.display ?? value.code ?? EM_DASH}</span>;
}

export function HumanNameDisplay({ value }: { value: HumanName }) {
  const assembled =
    value.text ??
    [value.prefix?.join(' '), value.given?.join(' '), value.family]
      .filter(Boolean)
      .join(' ');
  return <span>{assembled || EM_DASH}</span>;
}

export function PeriodDisplay({ value }: { value: Period }) {
  return (
    <span>
      {value.start ?? EM_DASH} – {value.end ?? 'now'}
    </span>
  );
}

export function RangeDisplay({ value }: { value: Range }) {
  return (
    <span>
      {value.low ? <QuantityDisplay value={value.low} /> : EM_DASH}
      {' – '}
      {value.high ? <QuantityDisplay value={value.high} /> : EM_DASH}
    </span>
  );
}

export function RatioDisplay({ value }: { value: Ratio }) {
  return (
    <span>
      {value.numerator ? <QuantityDisplay value={value.numerator} /> : EM_DASH}
      {' / '}
      {value.denominator ? (
        <QuantityDisplay value={value.denominator} />
      ) : (
        EM_DASH
      )}
    </span>
  );
}

export function IdentifierDisplay({ value }: { value: Identifier }) {
  return (
    <span>
      {value.system ? `${value.system}: ` : ''}
      {value.value ?? EM_DASH}
    </span>
  );
}

export function AnnotationDisplay({ value }: { value: Annotation }) {
  return <span>{value.text ?? EM_DASH}</span>;
}

export function ContactPointDisplay({ value }: { value: ContactPoint }) {
  return (
    <span>
      {value.system ? `${value.system}: ` : ''}
      {value.value ?? EM_DASH}
    </span>
  );
}

export function AddressDisplay({ value }: { value: Address }) {
  const assembled =
    value.text ??
    [value.line?.join(', '), value.city, value.state, value.postalCode]
      .filter(Boolean)
      .join(', ');
  return <span>{assembled || EM_DASH}</span>;
}

export function AttachmentDisplay({ value }: { value: Attachment }) {
  return (
    <span>{value.title ?? value.url ?? value.contentType ?? EM_DASH}</span>
  );
}

export function ReferenceDisplay({ value }: { value: Reference }) {
  return <span>{value.display ?? value.reference ?? EM_DASH}</span>;
}

const PRIMITIVE_TYPES = new Set([
  'string',
  'code',
  'id',
  'uri',
  'url',
  'canonical',
  'oid',
  'uuid',
  'markdown',
  'base64Binary',
  'date',
  'dateTime',
  'instant',
  'time',
  'boolean',
  'integer',
  'positiveInt',
  'unsignedInt',
  'decimal',
]);

function primitiveText(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Dispatch a value to its datatype display by the spec-declared type name.
 * Unknown/complex-unsupported types degrade to nothing rather than guessing.
 */
export function datatypeDisplay(type: string, value: unknown): ReactNode {
  if (isNil(value)) return null;
  if (PRIMITIVE_TYPES.has(type)) return <span>{primitiveText(value)}</span>;
  switch (type) {
    case 'Quantity':
    case 'Age':
    case 'Duration':
    case 'Count':
    case 'Distance':
    case 'Money':
      return <QuantityDisplay value={value as Quantity} />;
    case 'CodeableConcept':
      return <CodeableConceptDisplay value={value as CodeableConcept} />;
    case 'Coding':
      return <CodingDisplay value={value as Coding} />;
    case 'HumanName':
      return <HumanNameDisplay value={value as HumanName} />;
    case 'Period':
      return <PeriodDisplay value={value as Period} />;
    case 'Range':
      return <RangeDisplay value={value as Range} />;
    case 'Ratio':
      return <RatioDisplay value={value as Ratio} />;
    case 'Identifier':
      return <IdentifierDisplay value={value as Identifier} />;
    case 'Annotation':
      return <AnnotationDisplay value={value as Annotation} />;
    case 'ContactPoint':
      return <ContactPointDisplay value={value as ContactPoint} />;
    case 'Address':
      return <AddressDisplay value={value as Address} />;
    case 'Attachment':
      return <AttachmentDisplay value={value as Attachment} />;
    case 'Reference':
      return <ReferenceDisplay value={value as Reference} />;
    default:
      return null;
  }
}
